import { useEffect, useState } from "react";
import { Add, Delete, Edit } from "@mui/icons-material";
import { Box, Divider, Stack, Typography } from "@mui/material";

import messages from "../../../constants/messages";
import { useRuleWizardContext } from "../../../providers/RuleWizardProvider";
import { CoAppTextButton } from "../../global/styled/global.styled";
import {
	RuleReactionSidebarContainer, RuleSidebarRow, RuleSidebarRowActions
} from "../styled/rule-sidebars.styled";

import RuleReactionDialog from "./RuleReactionDialog";

export default function RuleReactionSidebar(props) {
	const { reactions, setReactions, editedId, ruleLocation, editedVersionOfRule, setStepDataHasChanged, handleSaveChanges } = useRuleWizardContext();

	const reactionFields = props.reactionFields;

	const [fieldDropdownValue, setFieldDropdownValue] = useState("");
	const [typeDropdownValue, setTypeDropdownValue] = useState("");
	const [reactionText, setReactionText] = useState("");
	const [isPopup, setIsPopup] = useState(false);
	const [formHasErrors, setFormHasErrors] = useState(false);
	const [userConfirmationText, setUserConfirmationText] = useState("");
	const [minimumCharacterCount, setMinimumCharacterCount] = useState(1);
	const [showConfirmationText, setShowConfirmationText] = useState(true);
	const [caseSensitive, setCaseSensitive] = useState(false);

	const [newReactions, setNewReactions] = useState(reactions);
	const [editReactionIndex, setEditReactionIndex] = useState([]);

	const [open, setOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);

	const [fieldValueError, setFieldValueError] = useState("");
	const [typeValueError, setTypeValueError] = useState("");
	const [reactionTextValueError, setReactionTextValueError] = useState("");
	const [userConfirmationTextValueError, setUserConfirmationTextValueError] = useState("");

	const clearFields = () => {
		setFieldDropdownValue("");
		setTypeDropdownValue("");
		setReactionText("");
		setIsPopup(false);
		setFieldValueError("");
		setTypeValueError("");
		setReactionTextValueError("");
		setUserConfirmationTextValueError("");
		setUserConfirmationText("");
		setMinimumCharacterCount(1);
	};

	const validateFields = () => {
		let hasErrors = false;

		if (typeDropdownValue === "silent") return true;

		if (typeDropdownValue === "") {
			setTypeValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
			hasErrors = true;
		} else {
			setTypeValueError("");
		}

		if (typeDropdownValue !== "disable" && (reactionText === "" || reactionText === "<p class=\"tiptap-paragraph\"></p>")) {
			setReactionTextValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
			hasErrors = true;
		} else {
			setReactionTextValueError("");
		}

		if (typeDropdownValue !== "" && !["popup", "userconfirmationpopup", "managerapprovalpopup", "freeresponsepopup"].includes(typeDropdownValue)) {
			if (fieldDropdownValue === "") {
				setFieldValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
				hasErrors = true;
			} else {
				setFieldValueError("");
			}
		}
		if (typeDropdownValue === "userconfirmationpopup") {
			if (userConfirmationText === "") {
				setUserConfirmationTextValueError(userConfirmationText === "" ? messages.FIELD_IS_REQUIRED_ERROR_MSG : "");
				hasErrors = true;
			} else {
				setUserConfirmationTextValueError("");
			}
		}

		if (hasErrors) {
			setFormHasErrors(true);
			return false;
		} else {
			return true;
		}
	};

	const handleClickOpen = () => {
		clearFields();
		setOpen(true);
	};

	const handleCancel = () => {
		clearFields();
		setOpen(false);
		setEditOpen(false);
	};

	const handleEditCancel = () => {
		clearFields();
		setEditOpen(false);
	};

	const handleClickOpenEditDialog = (reaction, index) => {
		setFieldDropdownValue(reaction?.reactionField?.name || "");
		setTypeDropdownValue(reaction.reactionType);
		setReactionText(reaction.reactionText);
		setIsPopup(reaction.isPopup);
		setUserConfirmationText(reaction.userConfirmationText);
		setEditReactionIndex(index);
		setMinimumCharacterCount(reaction.minimumCharacterCount);
		setCaseSensitive(reaction.caseSensitive);
		setShowConfirmationText(reaction.showConfirmationText);
		setEditOpen(true);
	};

	const handleEditSave = () => {
		//validate fields before updating redux
		if (!validateFields()) {
			return;
		} else {
			const selectedField = reactionFields.find(field => field.name === fieldDropdownValue);
			let updatedReaction = {
				reactionField: selectedField ? selectedField : null,
				reactionType: typeDropdownValue,
				reactionText: reactionText,
				isPopup: isPopup,
				userConfirmationText: userConfirmationText,
				minimumCharacterCount: minimumCharacterCount,
				caseSensitive: caseSensitive,
				showConfirmationText: showConfirmationText,
			};
			let nr = [...newReactions];
			nr.splice(editReactionIndex, 1, updatedReaction);
			setNewReactions(nr);
			setReactions(nr);
			setEditReactionIndex("");
			setEditOpen(false);
			handleSaveChanges(null, null,nr);

			if (editedId) {
				setStepDataHasChanged(true);
			}
		}

	};

	const handleDeleteReaction = (index) => {
		let reactionIndex = index ? index : editReactionIndex;
		let nr = [...newReactions];
		nr.splice(reactionIndex, 1);
		setNewReactions(nr);
		setEditReactionIndex("");
		setReactions(nr);
		setEditOpen(false);
		handleSaveChanges(null, null, nr);

		if (editedId) {
			setStepDataHasChanged(true);
		}
	};

	const handleSave = () => {
		if (!validateFields()) {
			return;
		} else {
			const selectedField = reactionFields.find(field => field.name === fieldDropdownValue);
			let newReaction = {
				reactionField: selectedField ? selectedField : null,
				reactionType: typeDropdownValue,
				reactionText: reactionText,
				isPopup: isPopup,
				userConfirmationText: userConfirmationText,
				minimumCharacterCount: minimumCharacterCount,
				caseSensitive: caseSensitive,
				showConfirmationText: showConfirmationText,
			};
			let nr = [...newReactions, newReaction];
			setNewReactions([...newReactions, newReaction]);
			setReactions(nr);
			setOpen(false);
			handleSaveChanges(null, null, nr);
		}
	};

	const handleFieldDropdownSelectionChange = (e) => {
		setFieldDropdownValue(e.target.value);
		if (e.target.value === "") {
			setFieldValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
			setFormHasErrors(true);
		} else {
			setFieldValueError("");
			setFormHasErrors(false);
		}
	};

	const handleTypeDropdownSelectionChange = (e) => {
		const newValue = e.target.value;
		setTypeDropdownValue(newValue);
		setTypeValueError("");
		setFormHasErrors(false);
		
		if (["popup", "userconfirmationpopup", "managerapprovalpopup", "freeresponsepopup"].includes(newValue)) {
			setIsPopup(true);
			setUserConfirmationText("");
			setMinimumCharacterCount(1);
			setReactionTextValueError("");
		} else {
			setIsPopup(false);
			setUserConfirmationText("");
			setMinimumCharacterCount(1);
			setFieldDropdownValue("");
			setReactionTextValueError("");
		}
	};

	const handleReactionTextInput = (value) => {
		setReactionText(value);
		if (value.length === 0 || value === "<p></p>") {
			setFormHasErrors(true);
			setReactionTextValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
		} else {
			setFormHasErrors(false);
			setReactionTextValueError("");
		}
	};

	const handleUserConfirmationTextInput = (e) => {
		setUserConfirmationText(e.target.value);
		if (e.target.value.length === 0) {
			setFormHasErrors(true);
			setUserConfirmationTextValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
		} else {
			setFormHasErrors(false);
			setUserConfirmationTextValueError("");
		}
	};

	const handleMinimumCharacterCountChange = (e) => {
		setMinimumCharacterCount(e.target.value);
	};

	const handleShowConfirmationTextChange = (e) => {
		setShowConfirmationText(e.target.checked);
	};

	const handleCaseSensitiveChange = (e) => {
		setCaseSensitive(e.target.checked);
	};

	useEffect(() => {
		if (props.editedId && Object.keys(editedVersionOfRule).length > 0) {
			if (editedVersionOfRule.ruleLocation) {
				if (ruleLocation.id !== editedVersionOfRule.ruleLocation.id || reactions.length !== editedVersionOfRule.rulereactions.length) {
					setStepDataHasChanged(true);
				}
			}
		}
	}, [ruleLocation, reactions]);

	return (
		<div>
			<RuleReactionSidebarContainer>
				<Box sx={{ display: "flex", justifyContent: "space-between" }}>
					<Typography sx={{ padding: "5px" }} variant="body1">Reactions</Typography>
					<CoAppTextButton onClick={handleClickOpen}><Add /></CoAppTextButton>
				</Box>
				<Divider sx={{ width: "100%" }} />
				<Stack alignItems="flexStart" direction="column" spacing="0.5" sx={{ maxHeight: "360px", overflow: "auto" }}>
					{
						newReactions.map((reaction, index) =>
							<div key={index}>
								<RuleSidebarRow>
									<div>
										{reaction.reactionField !== null && reaction.reactionType !== "silent" ?
											<Typography sx={{ fontWeight: "600" }} variant="body2">Field: {reaction.reactionField.name}</Typography>
											:
											null
										}
										<Typography variant="body2">Reaction: {props.reactionTypes.filter(type => type.name === reaction.reactionType)[0].friendlyName}</Typography>
									</div>
									<RuleSidebarRowActions alignItems="flexStart" direction="row">
										<Edit onClick={() => handleClickOpenEditDialog(reaction, index)} sx={{ fontSize: "medium", color: "#1D38BD", marginRight: "10px" }} />
										<Delete onClick={() => handleDeleteReaction(index)} sx={{ fontSize: "medium", color: "#1D38BD" }} />
									</RuleSidebarRowActions>
								</RuleSidebarRow>
								<Divider sx={{ width: "100%" }} />
							</div>
						)
					}
				</Stack>
			</RuleReactionSidebarContainer>

			<RuleReactionDialog
				isEdit={editOpen}
				isOpen={open}
				editReactionIndex={editReactionIndex}
				dialogCancelHandler={handleCancel}
				fieldValue={fieldDropdownValue}
				fieldDropdownValueChangeHandler={handleFieldDropdownSelectionChange}
				reactionFields={reactionFields}
				reactionText={reactionText}
				userConfirmationText={userConfirmationText}
				typeValue={typeDropdownValue}
				typeDropdownValueChangeHandler={handleTypeDropdownSelectionChange}
				reactionTextInputHandler={handleReactionTextInput}
				userConfirmationTextInputHandler={handleUserConfirmationTextInput}
				saveReactionHandler={handleSave}
				editSaveReactionHandler={handleEditSave}
				deleteReactionHandler={handleDeleteReaction}
				editCancelHandler={handleEditCancel}
				fieldValueError={fieldValueError}
				typeValueError={typeValueError}
				reactionTextValueError={reactionTextValueError}
				formHasErrors={formHasErrors}
				userConfirmationTextValueError={userConfirmationTextValueError}
				isLocationIframe={ruleLocation.iframe}
				reactionTypes={props.reactionTypes}
				currentReactions={reactions}
				minimumCharacterCount={minimumCharacterCount}
				handleMinimumCharacterCountChange={handleMinimumCharacterCountChange}
				showConfirmationText={showConfirmationText}
				handleShowConfirmationTextChange={handleShowConfirmationTextChange}
				caseSensitive={caseSensitive}
				handleCaseSensitiveChange={handleCaseSensitiveChange}
			/>
		</div>
	);
}